import { useForm, useFieldArray } from "react-hook-form";
import { IProduct } from "../../Interfaces/product";
import { useProductQuery } from "../../hooks/useReactQuery";
import { useState } from "react";
import toast from "react-hot-toast";

const CreateProduct = () => {
  const { createMutation } = useProductQuery();
  const [productMainFiles, setProductMainFiles] = useState<string[]>([]);
  const [variantProductFiles, setVariantProductFiles] = useState<string[][]>(
    []
  );

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IProduct>({
    defaultValues: {
      name: "",
      description: "",
      brand: "",
      category: "",
      price: 0,
      isActive: true,
      variantOptions: [
        { color: "", images: [""], sizes: [{ size: 0, stock: 0 }] },
      ],
    },
  });

  const {
    fields: variantFields,
    append: addVariant,
    remove: removeVariant,
  } = useFieldArray({
    control,
    name: "variantOptions",
  });

  const handleProductMainFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const readers = files.map((file) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      Promise.all(readers)
        .then((results) => {
          console.log("results", results);
          setProductMainFiles(results);
        })
        .catch((error) => {
          console.error("Error reading files:", error);
        });
    }
  };

  const handleProductVariantFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    variantIndex: number
  ) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const readers = files.map((file) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      Promise.all(readers)
        .then((results) => {
          setVariantProductFiles((prev) => {
            const updated = [...prev];
            updated[variantIndex] = results;
            return updated;
          });
        })
        .catch((error) => {
          console.error("Error reading files:", error);
        });
    }
  };

  const handleCreateProduct = async (payload: IProduct) => {
    payload.variantOptions = payload.variantOptions.map((variant, index) => ({
      ...variant,
      images: variantProductFiles[index] || [],
    }));
    payload.images = productMainFiles;

    createMutation
      .mutateAsync(payload)
      .then((res) => {
        console.log("product created", res);
        toast.success(res.message);
      })
      .catch((err) => {
        console.error("error", err);
      });
  };

  return (
    <div className="max-w-2xl md:mx-auto my-10 p-6 bg-white rounded-lg shadow-sm mx-3">
      <h2 className="text-2xl font-bold mb-4 text-center">Add Product</h2>
      <form
        onSubmit={handleSubmit(handleCreateProduct)}
        className="space-y-4 address_form"
      >
        <div className="flex justify-between items-center gap-5">
          <div className="flex flex-col gap-1 input_group">
            <label htmlFor="name">Name*</label>
            <input
              type="text"
              id="name"
              placeholder="Enter your product name"
              className="input_field"
              {...register("name", { required: true })}
            />
            <div className="field_error">
              {errors.name && <div>Product name is required</div>}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center gap-5">
          <div className="flex flex-col gap-1 input_group">
            <label htmlFor="description">Description*</label>
            <input
              type="text"
              id="description"
              placeholder="Enter product description"
              className="input_field"
              {...register("description", { required: true })}
            />
            <div className="field_error">
              {errors.description && <div>Product description is required</div>}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center gap-5">
          <div className="flex flex-col gap-1 input_group">
            <label htmlFor="brand">Brand*</label>
            <input
              type="text"
              id="brand"
              placeholder="Enter product brand"
              className="input_field"
              {...register("brand", { required: true })}
            />
            <div className="field_error">
              {errors.brand && <div>Product brand is required</div>}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center gap-5">
          <div className="flex flex-col gap-1 input_group">
            <label htmlFor="category">Category*</label>
            <input
              type="text"
              id="category"
              placeholder="Enter product category"
              className="input_field"
              {...register("category", { required: true })}
            />
            <div className="field_error">
              {errors.category && <div>Product category is required</div>}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center gap-5">
          <div className="flex flex-col gap-1 input_group">
            <label htmlFor="Price">Price*</label>
            <input
              type="number"
              id="Price"
              placeholder="Enter product Price"
              className="input_field"
              {...register("price", { required: true, setValueAs: Number })}
            />
            <div className="field_error">
              {errors.price && <div>Product Price is required</div>}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center gap-5">
          <div className="flex flex-col gap-1 input_group">
            <label htmlFor="images">Images*</label>
            <input
              type="file"
              id="images"
              placeholder="Enter product images"
              className="input_field"
              multiple
              onChange={handleProductMainFileChange}
            />
            <div className="field_error">
              {errors.images && <div>Product images is required</div>}
            </div>
          </div>
        </div>

        <div>
          <label className="block font-medium">Is Active?</label>
          <input type="checkbox" {...register("isActive")} className="ml-2" />
        </div>

        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold">Variant Options</h3>

          {variantFields.map((variant, index) => {
            return (
              <div
                key={variant.id}
                className="border p-4 mt-2 rounded flex flex-col gap-5"
              >
                <div className="flex justify-between items-center gap-5">
                  <div className="flex flex-col gap-1 input_group">
                    <label htmlFor="color">Color*</label>
                    <input
                      type="text"
                      id="color"
                      placeholder="Enter product color"
                      className="input_field"
                      {...register(`variantOptions.${index}.color`, {
                        required: true,
                      })}
                    />
                    <div className="field_error">
                      {errors.variantOptions?.[index]?.color && (
                        <div>Product Color is required</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center gap-5">
                  <div className="flex flex-col gap-1 input_group">
                    <label htmlFor="images">Images*</label>
                    <input
                      type="file"
                      id="images"
                      placeholder="Enter product images"
                      className="input_field"
                      multiple
                      onChange={(e) => handleProductVariantFileChange(e, index)}
                    />
                    <div className="field_error">
                      {errors.variantOptions?.[index]?.images && (
                        <div>Product images is required</div>
                      )}
                    </div>
                  </div>
                </div>

                <VariantSizes
                  control={control}
                  register={register}
                  variantIndex={index}
                />

                <button
                  type="button"
                  onClick={() => removeVariant(index)}
                  className="mt-2 text-red-500 hover:underline  w-fit text-left"
                >
                  Remove Variant
                </button>
              </div>
            );
          })}

          <div
            // type="button"
            onClick={() =>
              addVariant({
                color: "",
                images: [""],
                sizes: [{ size: 0, stock: 0 }],
              })
            }
            className="mt-1 text-green-500 px-4 py-2 rounded"
          >
            + Add Variant
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-[#4a69e2] text-white p-2 rounded hover:bg-blue-600"
        >
          Create Product
        </button>
      </form>
    </div>
  );
};

export default CreateProduct;

const VariantSizes = ({ control, register, variantIndex }: any) => {
  const {
    fields: sizesFields,
    append: addSize,
    remove: removeSize,
  } = useFieldArray({
    control,
    name: `variantOptions.${variantIndex}.sizes`,
  });

  return (
    <div className="flex flex-col gap-2">
      {sizesFields.map((size, sizeIndex) => (
        <div key={size.id} className="flex gap-2 my-1 justify-between">
          <div className="flex justify-between items-center gap-5">
            <div className="flex flex-col gap-1 input_group">
              <label htmlFor="size">Sizes*</label>
              <input
                type="number"
                id="size"
                placeholder="Enter product size"
                className="input_field"
                {...register(`variantOptions.${sizeIndex}.sizes.size`, {
                  required: true,
                  setValueAs: Number,
                })}
              />
              {/* <div className="field_error">
                    {errors.variantOptions?.[index]?.sizes?.size && <div>Product size is required</div>}
                  </div> */}
            </div>
            <div className="flex flex-col gap-1 input_group">
              <label htmlFor="stock">Stock*</label>
              <input
                type="number"
                id="stock"
                placeholder="Enter product stock"
                className="input_field"
                {...register(`variantOptions.${sizeIndex}.sizes.stock`, {
                  required: true,
                  setValueAs: Number,
                })}
              />
              {/* <div className="field_error">
                    {errors.variantOptions?.[index]?.sizes?.size && <div>Product size is required</div>}
                  </div> */}
            </div>
          </div>
          <button
            type="button"
            onClick={() => removeSize(sizeIndex)}
            className="mt-2 text-red-500 hover:underline  w-fit text-left"
          >
            Remove Size
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => addSize({ size: 0, stock: 0 })}
        // className="mt-3 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 w-fit"
        className="mt-1 text-green-500 px-4 py-2 rounded w-fit"
      >
        + Add Size
      </button>
    </div>
  );
};
